import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tag, Clock, Zap, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../context/AuthContext';
import { toast } from 'sonner';

const formatStartDate = (s) => {
  if (!s) return '';
  const datePart = s.includes('T') ? s.split('T')[0] : s;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const response = await api.get('/api/promotions/active');
        setPromotions(response.data);
      } catch (error) {
        console.error('Error fetching promotions:', error);
        toast.error('Failed to load promotions');
      } finally {
        setLoading(false);
      }
    };
    fetchPromotions();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading promotions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <Tag className="w-16 h-16 text-lime-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-lime-600">
              Current Promotions
            </span>
          </h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Check out our latest deals and special offers. Save on services and memberships!
          </p>
        </div>

        {/* Active Promotions */}
        {promotions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {promotions.map(promo => {
              const color = promo.id % 2 === 0 ? 'lime' : 'purple';
              const discountDisplay = promo.discount_type === 'percentage' 
                ? `${promo.discount_value}%` 
                : `$${promo.discount_value}`;
              const isUpcoming = promo.status === 'upcoming';
              
              return (
                <Card 
                  key={promo.id}
                  className={`bg-gradient-to-br ${
                    isUpcoming
                      ? 'from-zinc-900 to-black border-zinc-700'
                      : color === 'lime' 
                      ? 'from-lime-900/40 to-black border-lime-500' 
                      : 'from-purple-900/40 to-black border-purple-500'
                  } relative overflow-hidden ${isUpcoming ? 'opacity-90' : ''}`}
                  data-testid={`promo-card-${promo.id}`}
                >
                  {/* Discount Badge */}
                  <div className="absolute top-4 right-4">
                    <Badge className={`${
                      isUpcoming
                        ? 'bg-zinc-700 text-zinc-200'
                        : color === 'lime' 
                        ? 'bg-lime-400 text-black' 
                        : 'bg-purple-500 text-white'
                    } text-2xl font-bold px-4 py-2`}>
                      {discountDisplay} OFF
                    </Badge>
                  </div>

                  {/* Coming Soon ribbon */}
                  {isUpcoming && (
                    <div className="absolute top-0 left-0 bg-amber-500 text-black text-[10px] font-bold px-3 py-1 rounded-br-lg flex items-center gap-1 z-10">
                      <CalendarClock className="w-3 h-3" />
                      COMING SOON
                    </div>
                  )}

                  <CardHeader className={isUpcoming ? 'pt-10' : ''}>
                    <CardTitle className={`text-2xl ${
                      isUpcoming ? 'text-white' : color === 'lime' ? 'text-lime-400' : 'text-purple-400'
                    }`}>
                      {promo.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <p className="text-white text-lg">{promo.description}</p>
                    
                    <div className="space-y-2">
                      {isUpcoming && promo.valid_from && (
                        <div className="flex items-center text-amber-400 text-sm font-semibold">
                          <CalendarClock className="w-4 h-4 mr-2" />
                          Starts {formatStartDate(promo.valid_from)}
                        </div>
                      )}
                      <div className="flex items-center text-zinc-300 text-sm">
                        <Clock className="w-4 h-4 mr-2" />
                        {promo.valid_days || 'Ongoing'}
                      </div>
                      
                      <div className="bg-zinc-900 p-3 rounded-lg border border-zinc-700">
                        <p className="text-zinc-400 text-xs mb-1">Promo Code:</p>
                        <p className={`font-mono text-lg font-bold ${isUpcoming ? 'text-zinc-300' : 'text-lime-400'}`}>
                          {promo.promo_code}
                        </p>
                      </div>
                    </div>

                    {isUpcoming ? (
                      <Button
                        disabled
                        className="w-full bg-zinc-700 text-zinc-400 cursor-not-allowed"
                      >
                        Available {formatStartDate(promo.valid_from)}
                      </Button>
                    ) : (
                      <Link to="/booking">
                        <Button className={`w-full ${
                          color === 'lime'
                            ? 'bg-lime-400 text-black hover:bg-lime-500'
                            : 'bg-purple-600 text-white hover:bg-purple-700'
                        }`}>
                          Book Now
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800 max-w-3xl mx-auto mb-12">
            <CardContent className="p-12 text-center">
              <Tag className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold mb-2">No Active Promotions</h3>
              <p className="text-zinc-400 mb-6">Check back soon for great deals and special offers!</p>
              <Link to="/booking">
                <Button className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700">
                  Book Appointment
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* How to Use */}
        <Card className="bg-zinc-900 border-zinc-800 max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="text-lime-400 text-center">How to Use Promo Codes</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-zinc-300">
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">1</span>
                <span>Book your appointment online or call us</span>
              </li>
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">2</span>
                <span>Mention the promo code when you arrive</span>
              </li>
              <li className="flex items-start">
                <span className="bg-lime-400 text-black rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5 flex-shrink-0">3</span>
                <span>Enjoy your discounted service!</span>
              </li>
            </ol>
            <p className="text-zinc-500 text-sm mt-6 text-center">
              * One promo code per visit. Cannot be combined with other offers.
            </p>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="text-center mt-12">
          <p className="text-zinc-400 mb-4">Ready to save?</p>
          <Link to="/booking">
            <Button className="bg-gradient-to-r from-lime-500 to-lime-600 text-black text-lg px-8 py-6 hover:from-lime-600 hover:to-lime-700">
              <Zap className="w-5 h-5 mr-2" />
              Book Your Appointment
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Promotions;
